import React, { useEffect, useState } from "react";
import apiClient, { formatApiError } from "@/api/client";
import { Plus, Trash2, X, MessageSquare, ShieldCheck, Sparkles, Loader2, Circle, Clock } from "lucide-react";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const EMPTY = { email: "", name: "", password: "", phone: "", title: "", role: "member" };

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso); const diff = (new Date() - d) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff/60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff/3600)} sa önce`;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function TeamPage() {
  const { user } = useAuth(); const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]); const [presence, setPresence] = useState({});
  const [open, setOpen] = useState(false); const [form, setForm] = useState(EMPTY); const [err, setErr] = useState("");
  const [profile, setProfile] = useState(null); const [profLoading, setProfLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState(""); const [aiLoading, setAiLoading] = useState(false);
  const nav = useNavigate();

  const load = () => Promise.all([
    apiClient.get("/team/workload").then(r => r.data),
    apiClient.get("/team/presence").then(r => r.data),
  ]).then(([w, p]) => { setItems(w); setPresence(p); });
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const create = async (e) => { e.preventDefault(); setErr(""); try { await apiClient.post("/team", { ...form, phone: form.phone || null, title: form.title || null }); setOpen(false); setForm(EMPTY); load(); } catch(er){ setErr(formatApiError(er)); } };
  const del = async (id) => { if (!window.confirm("Üye silinsin mi?")) return; try { await apiClient.delete(`/team/${id}`); load(); } catch(er){ alert(formatApiError(er)); } };

  const openProfile = async (m) => {
    if (!isAdmin) return;
    setProfile({ user: m, stats: null, recent_tasks: [] }); setAiInsight(""); setProfLoading(true);
    try { const { data } = await apiClient.get(`/team/${m.id}/profile`); setProfile(data); }
    catch (e) { console.error(e); }
    finally { setProfLoading(false); }
  };

  const genAiInsight = async () => {
    if (!profile?.user) return;
    setAiLoading(true); setAiInsight("");
    try {
      const { data } = await apiClient.post("/ai/chat", {
        message: `${profile.user.name} adlı ekip üyemiz hakkında kısa bir performans analizi yap. Açık görev: ${profile.stats?.open_tasks||0}, tamamlanan: ${profile.stats?.done_tasks||0}, kritik: ${profile.stats?.critical_tasks||0}, üretkenlik: %${profile.stats?.productivity_score||0}. 3-4 cümle Türkçe.`,
        session_id: `team-insight-${profile.user.id}`,
      });
      setAiInsight(data.response || data.reply || data.message || "");
    } catch (e) {
      setAiInsight(`AI önerisi alınamadı: ${formatApiError(e)}`);
    } finally { setAiLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="team-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h1 className="text-2xl font-bold">Ekip</h1><p className="text-sm text-[#64748B] mt-1">{items.length} üye · {Object.values(presence).filter(p => p.online).length} çevrimiçi</p></div>
        {isAdmin && <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="ky-btn ky-btn-accent" data-testid="add-member-btn"><Plus size={14}/>Yeni Üye</button>}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(m => {
          const p = presence[m.id] || {};
          return (
            <div key={m.id} className={`ky-card p-5 ky-card-hover ${isAdmin ? "cursor-pointer" : ""}`} onClick={() => openProfile(m)} data-testid={`team-card-${m.id}`}>
              <div className="flex items-start gap-3">
                <div className="relative shrink-0">
                  <Avatar name={m.name} color={m.avatar_color} size={44}/>
                  {p.online && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#10B981] border-2 border-white" data-testid={`team-online-${m.id}`}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold">{m.name}</div>
                    {m.role === "admin" && <span className="ky-badge ky-badge-indigo"><ShieldCheck size={9}/>Admin</span>}
                  </div>
                  {m.title && <div className="text-xs text-[#4F46E5] font-medium mt-0.5">{m.title}</div>}
                  <div className="text-xs text-[#64748B] mt-0.5 truncate">{m.email}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-1">
                    {p.online ? <span className="text-[#10B981] font-medium flex items-center gap-1"><Circle size={6} className="fill-current"/>Çevrimiçi</span> : <><Clock size={10}/>{p.last_seen ? `Son: ${relTime(p.last_seen)}` : "Hiç giriş yapmadı"}</>}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#ECECEF] grid grid-cols-3 gap-2">
                <Mini label="Açık Görev" value={m.open_tasks} />
                <Mini label="Aktif Hizmet" value={m.active_services} />
                <Mini label="Kritik" value={m.critical_tasks} tone={m.critical_tasks > 0 ? "danger" : ""} />
              </div>
              <div className="mt-3 flex gap-2" onClick={e => e.stopPropagation()}>
                {m.id !== user?.id && <button onClick={() => nav(`/mesajlar?u=${m.id}`)} className="ky-btn ky-btn-outline ky-btn-sm flex-1"><MessageSquare size={11}/>Mesaj</button>}
                {isAdmin && m.id !== user?.id && <button onClick={() => del(m.id)} className="ky-btn ky-btn-ghost ky-btn-sm text-[#EF4444]" data-testid={`delete-member-${m.id}`}><Trash2 size={11}/></button>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Profile detail drawer */}
      {profile && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setProfile(null)}>
          <div className="flex-1 bg-black/40"/>
          <div onClick={e => e.stopPropagation()} className="w-full sm:w-[560px] bg-white flex flex-col ky-slide-in" data-testid="team-profile-drawer">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative shrink-0"><Avatar name={profile.user?.name} color={profile.user?.avatar_color} size={36}/>{presence[profile.user?.id]?.online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-white"/>}</div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{profile.user?.name}</div>
                  <div className="text-xs text-[#64748B] truncate">{profile.user?.title || profile.user?.email}</div>
                </div>
              </div>
              <button onClick={() => setProfile(null)} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {profLoading && !profile.stats && <div className="text-sm text-[#94A3B8] flex items-center gap-2"><Loader2 size={13} className="animate-spin"/>Yükleniyor…</div>}

              {profile.stats && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <StatBig label="Üretkenlik" value={`%${profile.stats.productivity_score}`} tone="indigo"/>
                    <StatBig label="Açık Görev" value={profile.stats.open_tasks}/>
                    <StatBig label="Tamamlanan" value={profile.stats.done_tasks} tone="success"/>
                    <StatBig label="Kritik" value={profile.stats.critical_tasks} tone={profile.stats.critical_tasks > 0 ? "danger" : ""}/>
                    <StatBig label="Aktif Hizmet" value={profile.stats.active_services}/>
                    <StatBig label="Biten Hizmet" value={profile.stats.completed_services}/>
                  </div>

                  <div className="ky-card p-4 bg-gradient-to-br from-[#FAFAFB] to-white">
                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold mb-2">Son Aktivite</div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between"><span className="text-[#94A3B8]">Son görülme</span><span className="font-medium">{relTime(presence[profile.user?.id]?.last_seen || profile.user?.last_seen)}</span></div>
                      <div className="flex justify-between"><span className="text-[#94A3B8]">Üye olma</span><span className="font-mono">{profile.user?.created_at ? new Date(profile.user.created_at).toLocaleDateString("tr-TR") : "—"}</span></div>
                      <div className="flex justify-between"><span className="text-[#94A3B8]">Rol</span><span className="font-medium">{profile.user?.role === "admin" ? "Admin" : "Ekip Üyesi"}</span></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2"><Sparkles size={13} className="text-[#7C3AED]"/><h4 className="text-sm font-semibold flex-1">AI Performans Analizi</h4>{!aiInsight && <button onClick={genAiInsight} disabled={aiLoading} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="team-ai-insight">{aiLoading && <Loader2 size={10} className="animate-spin"/>}Üret</button>}</div>
                    {aiInsight ? <div className="p-3 bg-gradient-to-br from-[#EEF2FF] to-white border border-[#C7D2FE] rounded-lg text-sm leading-relaxed whitespace-pre-wrap" data-testid="team-ai-insight-text">{aiInsight}</div> : <div className="text-xs text-[#94A3B8]">AI ile bu üyenin performansı hakkında kısa bir özet üretin.</div>}
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold mb-2">Son Görevler ({profile.recent_tasks?.length || 0})</div>
                    <div className="space-y-1.5">
                      {(profile.recent_tasks || []).slice(0, 8).map(t => (
                        <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-[#FAFAFB] rounded-md text-xs">
                          <span className={`ky-dot ${t.status === "tamamlandi" ? "bg-[#10B981]" : t.priority === "kritik" ? "bg-[#EF4444]" : "bg-[#4F46E5]"}`}/>
                          <span className="flex-1 truncate">{t.title}</span>
                          <span className="text-[#94A3B8] text-[10px]">{t.status}</span>
                        </div>
                      ))}
                      {(!profile.recent_tasks || profile.recent_tasks.length === 0) && <div className="text-xs text-[#94A3B8]">Görev geçmişi yok.</div>}
                    </div>
                  </div>

                  {profile.user?.id !== user?.id && (
                    <button onClick={() => { setProfile(null); nav(`/mesajlar?u=${profile.user.id}`); }} className="ky-btn ky-btn-outline w-full" data-testid="profile-message-btn">
                      <MessageSquare size={12}/>Mesaj gönder
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setOpen(false)}>
          <div className="flex-1 bg-black/40"/>
          <form onSubmit={create} onClick={e=>e.stopPropagation()} className="w-full sm:w-[480px] bg-white flex flex-col ky-slide-in" data-testid="member-form">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between"><h3 className="font-semibold">Yeni Ekip Üyesi</h3><button type="button" onClick={() => setOpen(false)} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button></div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {err && <div className="text-sm px-3 py-2 rounded-md bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]">{err}</div>}
              {[["name","Ad Soyad","text",true],["email","E-posta","email",true],["password","Şifre","password",true],["title","Unvan","text"],["phone","Telefon","text"]].map(([k,l,t,req]) => (
                <div key={k}><label className="block text-xs font-medium text-[#64748B] mb-1.5">{l}{req && " *"}</label><input type={t} required={req} value={form[k]||""} onChange={e=>setForm({...form,[k]:e.target.value})} className="ky-input" data-testid={`member-input-${k}`}/></div>
              ))}
              <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Rol</label><select value={form.role} onChange={e=>setForm({...form,role:e.target.value})} className="ky-select"><option value="member">Ekip Üyesi</option><option value="admin">Admin</option></select></div>
            </div>
            <div className="px-5 py-4 border-t border-[#ECECEF] flex gap-2"><button type="button" onClick={() => setOpen(false)} className="ky-btn ky-btn-outline flex-1">İptal</button><button type="submit" className="ky-btn ky-btn-accent flex-1" data-testid="save-member">Oluştur</button></div>
          </form>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, tone }) { return <div className="text-center"><div className="text-[10px] uppercase text-[#94A3B8] font-semibold tracking-wider">{label}</div><div className={`text-lg font-semibold tabular-nums mt-0.5 ${tone==="danger"?"text-[#EF4444]":"text-[#0F172A]"}`}>{value}</div></div>; }
function StatBig({ label, value, tone }) { const cls = tone === "success" ? "text-[#10B981]" : tone === "danger" ? "text-[#EF4444]" : tone === "indigo" ? "text-[#4F46E5]" : "text-[#0F172A]"; return <div className="ky-card p-4"><div className="text-[10px] uppercase text-[#94A3B8] font-semibold tracking-wider">{label}</div><div className={`text-2xl font-semibold tabular-nums mt-1 ${cls}`}>{value}</div></div>; }
